import { a3Copy } from "@/components/assignments/a3-copy";
import { ScaleEditor } from "./scale/Scale";
import { RubricLevelsFields } from "./RubricLevelsFields";
import { scaleMaximum } from "./scale/model";
/**
 * A piece of session work's facts, edited where they are shown: on the session
 * tile's row and in the record's brief. A draft saves each fact as it is left
 * or chosen. Published terms are held as typed until one correction, with its
 * reason, carries them. Closed work has no editor.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { courseTimezoneLabel } from "@/lib/course-schedule";
import type { AssessmentKind, ExpectedDeliverable } from "@/lib/api/assignments";
import { EXPECTED_DELIVERABLE_LABEL } from "@/lib/assignment-presentation";

import { useWorkOptions, type WorkChoices } from "./use-work-options";
import { ASSESSMENT_KIND_LABEL, NOT_GRADED, WHOLE_COURSE, levelFieldProblems, rubricActive, rubricTotal, workConflictFacts, type WorkFact, type WorkRecord } from "./work-facts";
import { pointsWord } from "./rubric-levels";
import type { SaveTarget, WorkSave } from "./use-work-save";

/** Where an editor's save shows, in plain words, with Retry when sending again can help. */
export function WorkSaveStatus({ engine }: { engine: WorkSave }) {
  const { state } = engine;
  const label = !state || state.kind === "conflict"
    ? ""
    : state.kind === "saving" ? a3Copy.saving : state.kind === "saved" ? a3Copy.saved : a3Copy.notSaved;
  return (
    <span className="flex items-center gap-1.5 text-meta text-muted-foreground">
      <span role="status">{label}</span>
      {state?.kind === "failed" && state.retry && (
        <Button type="button" variant="link" size="sm" className="px-0" onClick={engine.retry}>{a3Copy.retry}</Button>
      )}
    </span>
  );
}

/** A refusal or an act's failure, said once, with the act that can help. */
export function WorkNoteLine({ engine }: { engine: WorkSave }) {
  const { note } = engine;
  if (!note) return null;
  return (
    <div role={note.alert ? "alert" : "status"} className="flex flex-wrap items-center gap-x-2 text-meta">
      <p className={note.alert ? "text-destructive" : "text-muted-foreground"}>{note.text}</p>
      {note.refresh && (
        <Button type="button" variant="link" size="sm" className="px-0" onClick={engine.refresh}>{a3Copy.refresh}</Button>
      )}
    </div>
  );
}

function FactMessage({ id, children }: { id: string; children: ReactNode }) {
  return <p id={id} role="alert" className="text-meta text-destructive">{children}</p>;
}

function Hint({ id, children }: { id: string; children: ReactNode }) {
  return <p id={id} className="text-meta text-muted-foreground">{children}</p>;
}

/** A choice field's options, keeping the saved value even before the list reads. */
function withCurrent(choices: WorkChoices, value: string, name: (id: string) => string) {
  if (!value || choices.options.some((option) => option.id === value)) return choices.options;
  return [{ id: value, name: name(value) }, ...choices.options];
}

export function WorkFacts({
  id,
  work,
  engine,
  timezone,
  gradebookHref,
  focusInstructions = false,
}: {
  id: string;
  work: WorkRecord;
  engine: WorkSave;
  /** The course zone: Due is entered and shown in it. */
  timezone: string;
  /** The course Gradebook, beside a linked grade item. */
  gradebookHref?: string;
  /** A work just added opens here with its instructions next. */
  focusInstructions?: boolean;
}) {
  const options = useWorkOptions(work.course_id, timezone);
  const instructionsRef = useRef<HTMLTextAreaElement | null>(null);
  const { draft, lifecycle } = engine;
  const closed = lifecycle === "closed";
  const zone = courseTimezoneLabel(timezone) || timezone;

  useEffect(() => {
    if (focusInstructions) instructionsRef.current?.focus();
  }, [focusInstructions]);

  const messageOf = (target: SaveTarget) =>
    engine.failureOf(target) ?? (target === "correction" ? null : engine.problems[target] || null);
  const described = (fact: WorkFact, hint?: string) => {
    const message = messageOf(fact);
    const ids = [message ? `${id}-${fact}-message` : null, hint ?? null].filter(Boolean).join(" ");
    return {
      "aria-invalid": message ? true : undefined,
      "aria-describedby": ids || undefined,
      message: message && <FactMessage id={`${id}-${fact}-message`}>{message}</FactMessage>,
    };
  };
  const typed = (fact: WorkFact, multiline = false) => {
    const { message, ...aria } = described(fact);
    return { field: { ...engine.commit(fact, multiline), ...aria }, message };
  };

  const title = typed("title");
  const instructions = typed("instructions", true);
  const points = typed("points");
  const due = typed("due");
  const assessment = typed("assessment");
  const setAssessment = (change: Partial<typeof draft.assessment>) => engine.setField("assessment", { ...draft.assessment, ...change });
  const criterionChange = (index: number, change: { label?: string; points?: string }) =>
    setAssessment({ rubric: draft.assessment.rubric.map((criterion, at) => at === index ? { ...criterion, ...change } : criterion) });
  // A rubric's criteria make the work's total; the typed score-only total waits aside.
  const byRubric = rubricActive(draft.assessment), rubricSum = byRubric ? rubricTotal(draft.assessment.rubric) : null;
  const levelPoints = levelFieldProblems(draft.assessment).points;

  const classHint = options.classes.failed
    ? a3Copy.theSessionsCouldNotBeRead
    : !options.classes.loading && options.classes.options.length === 0 ? a3Copy.thisCourseHasNoSessionsYet : null;
  const classField = described("class", classHint ? `${id}-class-hint` : undefined);
  const gradeHint = options.gradeItems.failed
    ? a3Copy.theGradeItemsCouldNotBeRead
    : !options.gradeItems.loading && options.gradeItems.options.length === 0 ? a3Copy.thisCourseHasNoGradeItemsYet : null;
  const gradeField = described("gradeItem", gradeHint ? `${id}-grade-hint` : undefined);
  const gradebookLink = gradebookHref && work.grade_item_id && (
    <Link to={gradebookHref} className="text-ui font-medium text-primary underline-offset-4 hover:underline">{a3Copy.openInGradebook}</Link>
  );

  const state = engine.state;
  const conflict = state?.kind === "conflict" && (
    <ConflictNotice
      object={a3Copy.thisWork}
      facts={workConflictFacts(draft, state.theirs, timezone, options)}
      onKeepMine={engine.keepMine}
      onLoadTheirs={engine.loadTheirs}
    />
  );
  const correctionMessage = engine.reasonProblem ?? engine.failureOf("correction");

  return (
    <div id={id} className="@container/work-facts grid min-w-0 gap-4 pt-3 pb-1.5">
      {engine.target !== "correction" && conflict}
      <fieldset disabled={closed} className="grid min-w-0 gap-4 @md/work-facts:grid-cols-2">
        <div className="grid min-w-0 gap-1.5 @md/work-facts:col-span-2">
          <Label htmlFor={`${id}-title`}>{a3Copy.title}</Label>
          <Input
            id={`${id}-title`}
            value={draft.title}
            onChange={(event) => engine.setField("title", event.target.value)}
            {...title.field}
          />
          {title.message}
        </div>
        <div className="grid min-w-0 gap-1.5 @md/work-facts:col-span-2">
          <Label htmlFor={`${id}-instructions`}>{a3Copy.instructions}</Label>
          <Textarea
            ref={instructionsRef}
            id={`${id}-instructions`}
            dir="auto"
            value={draft.instructions}
            onChange={(event) => engine.setField("instructions", event.target.value)}
            {...instructions.field}
          />
          {instructions.message}
        </div>
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-kind`}>{a3Copy.kind}</Label>
          <NativeSelect id={`${id}-kind`} value={draft.assessment.kind} onChange={event => {
            const kind = event.target.value as AssessmentKind;
            setAssessment({ kind, scale: kind === "scored" ? draft.assessment.scale : null });
            assessment.field.onBlur();
          }}>
            {Object.entries(ASSESSMENT_KIND_LABEL).filter(([value]) => value !== "native_quiz" && (value !== "external_quiz" || work.assessment_kind === "external_quiz")).map(([kind, label]) => <option key={kind} value={kind}>{label}</option>)}
          </NativeSelect>
        </div>
        {draft.assessment.kind === "external_quiz" ? <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-quiz`}>{a3Copy.quizLink}</Label>
          <Input id={`${id}-quiz`} type="url" value={draft.assessment.externalUrl} onChange={event => setAssessment({ externalUrl: event.target.value })} {...assessment.field} aria-describedby={[assessment.field["aria-describedby"], `${id}-quiz-hint`].filter(Boolean).join(" ")} />
          <Hint id={`${id}-quiz-hint`}>{a3Copy.itsHostGovernsAccessTheScoreIsRecordedInIMAM}</Hint>
        </div> : draft.assessment.kind === "in_class" ? <p className="self-center text-meta text-muted-foreground">{a3Copy.nothingToHandInTheTeacherRecordsTheScore}</p>
          : <div className="grid min-w-0 gap-1.5">
            <Label htmlFor={`${id}-deliverable`}>{a3Copy.whatLearnersHandIn}</Label>
            <NativeSelect id={`${id}-deliverable`} value={draft.deliverable} onChange={event => engine.setField("deliverable", event.target.value as ExpectedDeliverable)}>
              {draft.deliverable === "" && <option value="" disabled>{a3Copy.anySubmission}</option>}
              {Object.entries(EXPECTED_DELIVERABLE_LABEL).filter(([kind]) => kind !== "none").map(([kind, label]) => <option key={kind} value={kind}>{label}</option>)}
              {draft.assessment.kind === "completion" && <option value="none">{a3Copy.nothingToHandIn}</option>}
            </NativeSelect>
          </div>}
        {draft.assessment.kind === "scored" && draft.assessment.scale && <section aria-label="Grading scale" className="@md/work-facts:col-span-2" onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) assessment.field.onBlur();
        }}><ScaleEditor scale={draft.assessment.scale} disabled={closed} onChange={scale => {
          setAssessment({ scale, rubric: [] });
          engine.setField("points", String(scaleMaximum(scale) ?? ""));
        }} /></section>}
        {draft.assessment.kind === "scored" && !draft.assessment.scale && <fieldset className="grid min-w-0 gap-3 @md/work-facts:col-span-2">
          <legend className="mb-2 text-ui font-medium">{a3Copy.rubric}</legend>
          {draft.assessment.rubric.map((criterion, index) => <div key={criterion.id} className="grid min-w-0 grid-cols-[minmax(0,1fr)_5rem] items-end gap-3 sm:grid-cols-[minmax(0,1fr)_6rem_auto]">
            <div className="grid min-w-0 gap-1.5">
              <Label htmlFor={`${id}-criterion-${criterion.id}`}>{a3Copy.criterion2}{index + 1}</Label>
              <Input id={`${id}-criterion-${criterion.id}`} value={criterion.label} onChange={event => criterionChange(index, { label: event.target.value })} {...assessment.field} />
            </div>
            <div className="grid min-w-0 gap-1.5">
              <Label htmlFor={`${id}-criterion-points-${criterion.id}`}>{a3Copy.points4}{index + 1}</Label>
              <Input id={`${id}-criterion-points-${criterion.id}`} type="number" min="0.01" step="0.01" value={criterion.points} onChange={event => criterionChange(index, { points: event.target.value })} {...assessment.field}
                {...(levelPoints[criterion.id] ? { "aria-invalid": true, "aria-describedby": `${id}-criterion-points-${criterion.id}-problem` } : {})} />
              {levelPoints[criterion.id] && <p id={`${id}-criterion-points-${criterion.id}-problem`} className="text-meta text-destructive">{levelPoints[criterion.id]}</p>}
            </div>
            <Button type="button" variant="subtle" className="col-span-2 justify-self-start whitespace-nowrap sm:col-auto" aria-label={a3Copy.removeCriterion2(index + 1)} onClick={() => {
              setAssessment({ rubric: draft.assessment.rubric.filter((_, at) => at !== index) });
              assessment.field.onBlur();
            }}>{a3Copy.remove}</Button>
          </div>)}
          <Button type="button" variant="subtle" className="justify-self-start" disabled={draft.assessment.rubric.length >= 20} onClick={() => setAssessment({ rubric: [...draft.assessment.rubric, { id: crypto.randomUUID(), label: "", points: "" }] })}>{a3Copy.criterion3}</Button>
          {draft.assessment.rubric.length > 0 && <RubricLevelsFields id={id} assessment={draft.assessment} disabled={closed} onChange={change => { setAssessment(change); }} />}
          <Hint id={`${id}-rubric-hint`}>{a3Copy.optionalCriterionPointsAddUpToTheWorkSPoints}</Hint>
        </fieldset>}
        <div className="empty:hidden @md/work-facts:col-span-2">{assessment.message}</div>
        {byRubric ? <div data-derived-points className="grid min-w-0 gap-1.5">
          <p className="text-ui font-medium text-foreground">{a3Copy.points}</p>
          <p className="text-ui text-foreground">{a3Copy.criteriaTotal(rubricSum == null ? null : pointsWord(rubricSum))}</p>
          {points.message}
        </div> : !(draft.assessment.kind === "scored" && draft.assessment.scale) && <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-points`}>{a3Copy.points}</Label>
          <Input
            id={`${id}-points`}
            type="number"
            inputMode="decimal"
            min={0}
            max={9999.99}
            step="0.01"
            value={draft.points}
            onChange={(event) => engine.setField("points", event.target.value)}
            {...points.field}
          />
          {points.message}
        </div>}
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-due`}>{a3Copy.due3}{zone}{a3Copy.closeParenthesis}</Label>
          <Input
            id={`${id}-due`}
            type="datetime-local"
            value={draft.due}
            onChange={(event) => engine.setField("due", event.target.value)}
            {...due.field}
          />
          {due.message}
        </div>
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-class`}>{a3Copy.session}</Label>
          <NativeSelect
            id={`${id}-class`}
            value={draft.unitId}
            disabled={options.classes.loading || options.classes.failed || (options.classes.options.length === 0 && !draft.unitId)}
            aria-invalid={classField["aria-invalid"]}
            aria-describedby={classField["aria-describedby"]}
            onChange={(event) => engine.setField("unitId", event.target.value)}
          >
            <option value="">{WHOLE_COURSE}</option>
            {withCurrent(options.classes, draft.unitId, options.classLabel).map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </NativeSelect>
          {classHint && <Hint id={`${id}-class-hint`}>{classHint}</Hint>}
          {classField.message}
        </div>
        {lifecycle === "draft" ? (
          <div className="grid min-w-0 gap-1.5 @md/work-facts:col-span-2">
            <Label htmlFor={`${id}-grade`}>{a3Copy.gradeItem}</Label>
            <NativeSelect
              id={`${id}-grade`}
              value={draft.gradeItemId}
              disabled={options.gradeItems.loading || options.gradeItems.failed || options.gradeItems.options.length === 0}
              aria-invalid={gradeField["aria-invalid"]}
              aria-describedby={gradeField["aria-describedby"]}
              onChange={(event) => engine.setField("gradeItemId", event.target.value)}
            >
              <option value="">{NOT_GRADED}</option>
              {withCurrent(options.gradeItems, draft.gradeItemId, options.gradeItemLabel).map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </NativeSelect>
            {gradeHint && <Hint id={`${id}-grade-hint`}>{gradeHint}</Hint>}
            {gradeField.message}
            {gradebookLink}
          </div>
        ) : (
          // Fixed once the work is published: learners were told what it counts toward.
          <div className="grid min-w-0 gap-1.5 @md/work-facts:col-span-2">
            <p className="text-ui font-medium text-foreground">{a3Copy.gradeItem}</p>
            <p className="text-ui text-foreground">
              {options.gradeItemLabel(work.grade_item_id ?? "")}
              {gradebookLink && <>{" · "}{gradebookLink}</>}
            </p>
          </div>
        )}
      </fieldset>

      {lifecycle !== "draft" && engine.held && (
        <div className="grid min-w-0 gap-3 border-t border-rule pt-3">
          {engine.target === "correction" && conflict}
          <fieldset disabled={closed} className="grid min-w-0 gap-1.5">
            <Label htmlFor={`${id}-reason`}>{a3Copy.whyDidTheTermsChange}</Label>
            <Textarea
              id={`${id}-reason`}
              className="min-h-16"
              value={engine.reason}
              aria-invalid={correctionMessage ? true : undefined}
              aria-describedby={[`${id}-reason-hint`, correctionMessage ? `${id}-reason-message` : null].filter(Boolean).join(" ")}
              onChange={(event) => engine.setReason(event.target.value)}
            />
            <Hint id={`${id}-reason-hint`}>{a3Copy.learnersSeeTheCorrectedTermsNotThisReason}</Hint>
            {correctionMessage && <FactMessage id={`${id}-reason-message`}>{correctionMessage}</FactMessage>}
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={closed || engine.busy || state?.kind === "saving"}
              onClick={engine.saveCorrection}
            >{a3Copy.saveCorrection}</Button>
            <Button type="button" size="sm" variant="ghost" disabled={state?.kind === "saving"} onClick={engine.discard}>{a3Copy.discardChanges}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
